import { useQuery } from "@tanstack/react-query";
import { pb, appMode } from "./pocketbase";

export interface FeedLink {
  /** The subscription URL, or null when the feed isn't available. */
  url: string | null;
  /** Why it isn't available, phrased for the person reading Settings. */
  reason?: string;
}

/**
 * The calendar subscription URL.
 *
 * ICS_TOKEN is a Worker secret, so the browser cannot construct the feed URL on
 * its own — it asks /calendar/link with the signed-in user's token and the
 * Worker answers only if that user is the feed's own account.
 *
 * A failure carries its reason back to the UI rather than just hiding the
 * feature. Silently showing nothing gives you no way to tell "not configured"
 * from "configured wrong", which costs far more than the line of text.
 *
 * Never retried: a 401/403/404 here is a settled answer, not a blip.
 */
export function useCalendarFeedUrl() {
  return useQuery({
    queryKey: ["calendar-feed-url"],
    enabled: appMode === "live",
    staleTime: Infinity,
    retry: false,
    queryFn: async (): Promise<FeedLink> => {
      const token = pb.authStore.token;
      if (!token) return { url: null, reason: "Not signed in." };

      const res = await fetch("/calendar/link", { headers: { Authorization: token } });
      if (res.ok) {
        const { url } = (await res.json()) as { url?: string };
        return { url: url ?? null };
      }

      let message = "";
      try {
        message = ((await res.json()) as { error?: string }).error ?? "";
      } catch {
        /* not JSON — the status alone still tells us which branch failed */
      }

      const email = (pb.authStore.model as { email?: string } | null)?.email;
      const who = res.status === 403 && email ? ` You are signed in as ${email}.` : "";
      return { url: null, reason: `${res.status}${message ? ` — ${message}` : ""}${who}` };
    },
  });
}

/**
 * The same feed narrowed to one project, which is how a project gets its own
 * colour and its own on/off switch in a calendar app.
 *
 * Uses the record id rather than the name: the name is editable, and a renamed
 * project would silently empty an existing subscription.
 */
export function projectFeedUrl(base: string, projectId: string): string {
  return `${base}?project=${encodeURIComponent(projectId)}`;
}
